/**
 * Single source of truth for supported locales.
 *
 * A "locale" here is a BCP 47 tag that carries both a language and a market.
 *   - `baseLang` is the language code used by i18next / CookieYes / Lyracom.
 *   - `currency` is the ISO 4217 code used for pricing and backend `currency` header.
 *   - `translationPath` is the folder under `public/locales/` that holds the JSON file.
 *
 * To add a new market (e.g. US English):
 *   { code: "en-US", baseLang: "en", currency: "USD", symbol: "$",
 *     symbolBefore: true, label: "English", translationPath: "en-US" }
 *
 * Legacy (non-BCP-47) codes live in `LEGACY_LOCALE_MAP` only. All redirects and
 * fallbacks route through it — never hardcode a legacy code anywhere else.
 */

export const SUPPORTED_LOCALES = [
  { code: "en",    baseLang: "en", currency: "EUR", symbol: "€", symbolBefore: true,  label: "English",  translationPath: "en",    routed: true  },
  { code: "en-GB", baseLang: "en", currency: "GBP", symbol: "£", symbolBefore: true,  label: "English",  translationPath: "en-GB", routed: true  },
  { code: "fr",    baseLang: "fr", currency: "EUR", symbol: "€", symbolBefore: false, label: "Francais", translationPath: "fr",    routed: true  },
  { code: "es",    baseLang: "es", currency: "EUR", symbol: "€", symbolBefore: false, label: "Español",  translationPath: "es",    routed: true  },
  { code: "it",    baseLang: "it", currency: "EUR", symbol: "€", symbolBefore: false, label: "Italiano", translationPath: "it",    routed: true  },
  { code: "de",    baseLang: "de", currency: "EUR", symbol: "€", symbolBefore: false, label: "Deutsch",  translationPath: "de",    routed: true  },
];

export const DEFAULT_LOCALE = "fr";

/**
 * Legacy URL/cookie/DB values that should be transparently redirected to a
 * supported locale. Drives the `/uk/*` → `/en-GB/*` redirect and cookie migration.
 */
export const LEGACY_LOCALE_MAP = {
  uk: "en-GB",
};

const LOCALE_BY_CODE = SUPPORTED_LOCALES.reduce((acc, l) => {
  acc[l.code] = l;
  return acc;
}, {});

// Precompute which baseLang groups have >1 locale — used by getLocaleLabel.
const BASE_LANG_GROUP_SIZE = SUPPORTED_LOCALES.reduce((acc, l) => {
  acc[l.baseLang] = (acc[l.baseLang] || 0) + 1;
  return acc;
}, {});

/** @returns the full locale object for `code`, or `undefined` if unsupported. */
export const getLocale = (code) => LOCALE_BY_CODE[code];

/** `true` for any supported or legacy code. */
export const isSupportedLocale = (code) =>
  code != null && (code in LOCALE_BY_CODE || code in LEGACY_LOCALE_MAP);

/** `"uk"` → `"en-GB"`; unchanged for any already-supported code. */
export const resolveLegacyLocale = (code) => LEGACY_LOCALE_MAP[code] ?? code;

/**
 * Base language for a route locale.
 *   "en-GB" → "en"; legacy "uk" → "en" (via LEGACY_LOCALE_MAP).
 *   Unknown codes fall back to DEFAULT_LOCALE's baseLang.
 * Use this for i18n.language, CookieYes, Lyracom, and the backend `lng` header.
 */
export const getBaseLang = (code) => {
  const resolved = resolveLegacyLocale(code);
  return LOCALE_BY_CODE[resolved]?.baseLang ?? LOCALE_BY_CODE[DEFAULT_LOCALE].baseLang;
};

/** ISO 4217 currency for `code`. Unknown → EUR. */
export const getCurrency = (code) => {
  const resolved = resolveLegacyLocale(code);
  return LOCALE_BY_CODE[resolved]?.currency ?? "EUR";
};

/** Currency symbol for `code`. Unknown → €. */
export const getCurrencySymbol = (code) => {
  const resolved = resolveLegacyLocale(code);
  return LOCALE_BY_CODE[resolved]?.symbol ?? "€";
};

/**
 * Translation folder path for `code` (the part after `public/locales/`).
 * Returns the raw code for unknown values — i18next will 404 and fall back.
 */
export const getTranslationPath = (code) => {
  const resolved = resolveLegacyLocale(code);
  return LOCALE_BY_CODE[resolved]?.translationPath ?? code;
};

/**
 * Disambiguated display label for the language dropdown.
 *   - Single-locale groups return the plain label ("Francais", "Deutsch").
 *   - Multi-locale groups append "(CURRENCY)": "English (EUR)", "English (GBP)".
 * Adding a new locale that shares a baseLang auto-disambiguates existing entries.
 */
export const getLocaleLabel = (code) => {
  const locale = LOCALE_BY_CODE[code];
  if (!locale) return code;
  if (BASE_LANG_GROUP_SIZE[locale.baseLang] > 1) {
    return `${locale.label} (${locale.currency})`;
  }
  return locale.label;
};

/**
 * Extract the locale segment from a URL pathname.
 *   "/en-GB/foo" → "en-GB"; "/uk/foo" → "uk" (legacy, caller decides what to do).
 *   Returns null for unknown segments.
 */
export const getLocaleFromPath = (pathname) => {
  const segment = pathname?.split("/")[1];
  return isSupportedLocale(segment) ? segment : null;
};

/** `"/en-GB/foo/bar"` → `"/foo/bar"`. Leaves path unchanged if no locale prefix. */
export const stripLocaleFromPath = (pathname) => {
  const segment = pathname?.split("/")[1];
  if (!isSupportedLocale(segment)) return pathname;
  return pathname.slice(segment.length + 1) || "/";
};

/**
 * Prefix a subpath with a locale. `withLocale("en-GB", "/login")` → `"/en-GB/login"`.
 * Leading slash on the subpath is optional.
 */
export const withLocale = (locale, subpath = "") => {
  const normalized = subpath.startsWith("/") ? subpath : `/${subpath}`;
  return `/${locale}${normalized === "/" ? "" : normalized}`;
};

/**
 * Format a price with the correct symbol placement.
 *   EN/EN-GB: "€49.99" / "£49.99"
 *   FR/DE/ES/IT: "49.99 €"
 */
export const formatPrice = (amount, code) => {
  const resolved = resolveLegacyLocale(code);
  const locale = LOCALE_BY_CODE[resolved] ?? LOCALE_BY_CODE[DEFAULT_LOCALE];
  return locale.symbolBefore
    ? `${locale.symbol}${amount}`
    : `${amount} ${locale.symbol}`;
};

/**
 * Backward-compat shim. Returns the `{ code, symbol, name, symbolBefore }` shape
 * that legacy call sites (payment, subscription, printing, etc.) expect.
 *
 * New code should use `getCurrency` / `getCurrencySymbol` directly.
 * @deprecated Prefer `getCurrency(code)` + `getCurrencySymbol(code)`.
 */
export const getCurrencyForLocale = (code) => {
  const resolved = resolveLegacyLocale(code);
  const locale = LOCALE_BY_CODE[resolved] ?? LOCALE_BY_CODE[DEFAULT_LOCALE];
  return {
    code: locale.currency,
    symbol: locale.symbol,
    symbolBefore: locale.symbolBefore,
    name: CURRENCY_NAMES[locale.currency] ?? locale.currency,
  };
};

const CURRENCY_NAMES = {
  EUR: "Euro",
  GBP: "British Pound",
};
